import React from 'react';
import useI18n from '@/hooks/useI18n';
import Tooltip from '@/components/common/ToolTip';
import IconButton from '@/components/common/IconButton';
import CampaignProgressBar from '@/components/common/CampaignProgressBar';
import { formatMoney } from '@/utils/numberUtils';

interface CampaignSummaryRowProps {
  brandName: string;
  dateRange: string;
  isLive: boolean;
  budget: number;
  remainingBudget: number;
  remainingPercent: number;
  platform: string;
  onShare: () => void;
}

/**
 * Campaign header inside the dashboard panel. Share is the only action here —
 * editing and pausing live on the campaign page behind "Go inside campaign".
 */
const CampaignSummaryRow: React.FC<CampaignSummaryRowProps> = ({
  brandName,
  dateRange,
  isLive,
  budget,
  remainingBudget,
  remainingPercent,
  platform,
  onShare,
}) => {
  const { t } = useI18n();

  return (
    <div
      data-testid="campaign-summary"
      className="flex w-full flex-col items-start justify-between gap-4 pb-4 xl:flex-row"
    >
      <div className="flex flex-col items-start gap-6 md:flex-row md:items-end md:gap-8">
        <div className="flex flex-col items-start gap-2">
          <p
            data-testid="campaign-brand-name"
            className="truncate font-header text-lg font-medium leading-tight text-[#121111] opacity-90"
          >
            {brandName}
          </p>
          <div className="flex items-center gap-2">
            <img
              src="/assets/fi_calendar.svg"
              alt=""
              className="h-4 w-4 shrink-0"
            />
            <span
              data-testid="campaign-date-range"
              className="font-header text-sm leading-[22px] tracking-[0.14px] text-black/70"
            >
              {dateRange}
            </span>
            <span
              data-testid="campaign-status"
              className="flex items-center gap-0.5 rounded-[49px] bg-[#FFA500]/10 px-2 py-1"
            >
              <span className="h-1 w-1 rounded-full bg-[#FFA500]" />
              <span className="font-header text-sm font-medium leading-4 text-[#FFA500]">
                {isLive ? t('common.live') : t('common.paused')}
              </span>
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-6 md:gap-8">
          <div className="flex flex-col items-start gap-2">
            <p className="font-header text-sm leading-4 text-[#9B919D]">
              {t('common.budget')}
            </p>
            <p
              data-testid="campaign-budget"
              className="font-header text-lg font-medium leading-none text-[#292828]"
            >
              {formatMoney(budget)}
            </p>
          </div>

          <div className="flex flex-col items-start gap-2">
            <p className="font-header text-sm leading-4 text-[#9B919D]">
              {t('common.remainingBalance')}
            </p>
            <div className="flex items-center gap-1">
              <p
                data-testid="campaign-remaining-budget"
                className="font-header text-lg font-medium leading-none text-[#292828]"
              >
                {formatMoney(remainingBudget)}
              </p>
              <div className="w-[160px]">
                <CampaignProgressBar
                  completed={remainingPercent}
                  height={8}
                  mainBgClass="bg-[#15803D]"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col items-start gap-2">
            <p className="font-header text-sm leading-4 text-[#9B919D]">
              {t('common.platform')}
            </p>
            <div
              data-testid="campaign-platforms"
              className="flex items-center gap-3"
            >
              {(platform === 'both' || platform === 'tiktok') && (
                <img
                  src="/assets/FullTikTok.svg"
                  alt="TikTok"
                  className="h-5"
                />
              )}
              {(platform === 'both' || platform === 'instagram') && (
                <img
                  src="/assets/FullInstagram.svg"
                  alt="Instagram"
                  className="h-5"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center self-end xl:self-start">
        <Tooltip text={String(t('links.share'))} position="top">
          <IconButton
            data-testid="campaign-share-button"
            src="/assets/svgs/fi_share.svg"
            // 42x42 target around a 24px glyph. Padding and radius go inline
            // because IconButton's own p-0.5/rounded-full would otherwise win.
            className="size-[42px]"
            style={{ padding: 9, borderRadius: 18 }}
            effectDarkness={2}
            onClick={onShare}
          />
        </Tooltip>
      </div>
    </div>
  );
};

export default CampaignSummaryRow;
